import { memo, Profiler, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Group } from '@mantine/core';
import { ChevronLeft } from 'lucide-react';
import { useHasPermission } from '../../../providers/AuthProvider';
import { useAdminUser } from '../../../hooks/useAdminUser';
import { UserInfoSection } from '../../../admin/components/UserInfoSection';
import {
  LazyCoursesTab,
  LazyCourseEditModal,
} from '../../../admin/components/LazyComponents';

const AdminUserCourses = memo(() => {
  const hasManageCourses = useHasPermission('manage_courses');
  const { id } = useParams<{ id: string }>();

  // Get admin user data and actions
  const adminUserData = useAdminUser();

  // TESTING PURPOSES ONLY
  const onRenderCallback = useCallback(
    (id: string, phase: string, actualDuration: number) => {
      if (actualDuration > 8) {
        // More aggressive threshold - half a frame
        console.warn(
          `🐌 Slow render detected in ${id}: ${phase} took ${actualDuration}ms`
        );
      }
    },
    []
  );

  if (!hasManageCourses) {
    return <div>Non hai i permessi per gestire i corsi</div>;
  }

  return (
    <Profiler id='AdminUserCourses' onRender={onRenderCallback}>
      <Group mb='md'>
        <Button
          variant='subtle'
          leftSection={<ChevronLeft size={16} />}
          component={Link}
          to={`/admin/users/${id}`}
        >
          Torna al Profilo Utente
        </Button>
      </Group>

      <UserInfoSection
        userInfo={adminUserData.userInfo}
        loadingUserInfo={adminUserData.loadingUserInfo}
      />

      <Profiler id='CoursesTab' onRender={onRenderCallback}>
        <LazyCoursesTab
          userCourses={adminUserData.userCourses}
          loadingCourses={adminUserData.loadingCourses}
          onEditCourse={adminUserData.setSelectedUserCourse}
        />
      </Profiler>

      <LazyCourseEditModal
        opened={adminUserData.selectedUserCourse !== undefined}
        onClose={() => adminUserData.setSelectedUserCourse(undefined)}
        course={adminUserData.selectedUserCourse}
        onSave={adminUserData.handleUpdateCourse}
      />
    </Profiler>
  );
});

AdminUserCourses.displayName = 'AdminUserCourses';

export default AdminUserCourses;
